import React, { useState, useEffect, useContext } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import ConfirmationModal from '../../components/common/ConfirmationModal';
import PaginatedTable from '../../components/PaginatedTable';
import TitleWithBack from '../../components/common/TitleWithBack';
import { formatDate } from '../../utils/helpers';
import { useDialog } from '../../context/DialogContext';
import { AuthContext } from '../../context/AuthContext';

function SeriesView() {
  const navigate = useNavigate();
  const { confirm } = useDialog();
  const { user } = useContext(AuthContext);
  const canCloseSeries = user && (user.role === 'archiver' || user.role === 'admin');
  const [seriesList, setSeriesList] = useState([]);
  // Completion summaries are fetched lazily, only for expired-and-not-yet-
  // closed series, keyed by series id: { totalAssignments, incompleteAssignments }
  const [completionSummaries, setCompletionSummaries] = useState({});
  const [keyword, setKeyword] = useState('');
  const [page, setPage] = useState(0);
  const [size] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [tooltip, setTooltip] = useState('');
  const [loading, setLoading] = useState(false);
  const [sortColumn, setSortColumn] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [deleteId, setDeleteId] = useState(null);

  // State to store active popover data: id, bounding rect, and programDetails
  const [activePopoverData, setActivePopoverData] = useState(null);

  useEffect(() => {
    fetchSeries();
  }, [page, keyword]);

  // Listen for clicks outside of the popover and cell to dismiss it.
  useEffect(() => {
    const handleDocumentClick = (e) => {
      if (
        !e.target.closest('.programs-cell') &&
        !e.target.closest('.programs-popover')
      ) {
        setActivePopoverData(null);
      }
    };

    document.addEventListener('click', handleDocumentClick);
    return () => {
      document.removeEventListener('click', handleDocumentClick);
    };
  }, []);

  // Only expired, not-yet-closed series need a completion check - active
  // series don't need this warning, so it isn't fetched for every row.
  useEffect(() => {
    const toFetch = seriesList.filter((s) => s.expired && !s.closed && !(s.id in completionSummaries));
    if (toFetch.length === 0) return;
    toFetch.forEach(async (s) => {
      try {
        const res = await axios.get(`/api/exam-series/${s.id}/completion-summary`);
        setCompletionSummaries((prev) => ({ ...prev, [s.id]: res.data }));
      } catch (err) {
        // Non-fatal - the row just won't show an incomplete-count badge.
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seriesList]);

  const fetchSeries = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/exam-series', { params: { page, size, keyword } });
      const data = res.data.content ? res.data.content : res.data;
      setSeriesList(data);
      setTotalPages(res.data.totalPages ? res.data.totalPages : 1);
    } catch (err) {
      setTooltip('Error fetching series.');
    }
    setLoading(false);
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`/api/exam-series/${id}`);
      setTooltip('Series deleted successfully!');
      fetchSeries();
      setDeleteId(null);
    } catch (err) {
      setTooltip('Error deleting series.');
    }
  };

  const handleClose = async (series) => {
    const summary = completionSummaries[series.id];
    const incomplete = summary?.incompleteAssignments || 0;
    const message = incomplete > 0
      ? `This series still has ${incomplete} incomplete assignment(s) out of ${summary.totalAssignments}. Closing stops it from syncing further but does not affect those assignments. Close anyway?`
      : 'Close this series? This stops it from syncing further from the API Gateway. This cannot be undone from here.';
    const ok = await confirm(message, { title: 'Close Exam Series', confirmText: 'Close Series', variant: 'danger' });
    if (!ok) return;
    try {
      // Sent back so the backend can verify this confirmation was actually
      // shown against the current count, not a stale page - see
      // ExamSeriesService.closeSeries. Omitted (rather than sent as 0) when
      // there's nothing incomplete, since the backend only checks it when
      // its own count is > 0.
      await axios.post(`/api/exam-series/${series.id}/close`, null, {
        params: incomplete > 0 ? { acknowledgedIncompleteCount: incomplete } : {},
      });
      setTooltip('Series closed successfully!');
      fetchSeries();
    } catch (err) {
      setTooltip(err.response?.data?.error || err.response?.data?.message || 'Error closing series.');
    }
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortOrder('asc');
    }
  };

  const sortedSeries = [...seriesList].sort((a, b) => {
    let valueA = a[sortColumn] || '';
    let valueB = b[sortColumn] || '';
    if (sortColumn === 'startDate' || sortColumn === 'endDate') {
      valueA = new Date(valueA);
      valueB = new Date(valueB);
    } else {
      valueA = valueA.toString().toLowerCase();
      valueB = valueB.toString().toLowerCase();
    }
    if (valueA < valueB) return sortOrder === 'asc' ? -1 : 1;
    if (valueA > valueB) return sortOrder === 'asc' ? 1 : -1;
    return 0;
  });

  // Update columns to show program combinations
  const columns = [
    { key: 'serial', header: <>#</> },
    { key: 'name', header: <>Series Name</> },
    { key: 'startDate', header: <>Start Date</> },
    { key: 'endDate', header: <>End Date</> },
    { key: 'programs', header: <>Program Combinations</> },
    { key: 'syncStatus', header: <>Gateway Sync</> },
    { key: 'actions', header: 'Actions' }
  ];

  const renderRow = (series, idx, pageIndex, pageSize) => {
    // Handle both new seriesPrograms structure and legacy programs structure
    let programDetails = [];
    
    if (series.seriesPrograms && Array.isArray(series.seriesPrograms)) {
      // New structure: seriesPrograms with program, year, semester
      programDetails = series.seriesPrograms.map(sp => {
        const programName = sp.program?.name || 'N/A';
        const yearName = sp.yearOfStudy?.name || 'N/A';
        const semesterName = sp.semester?.name || 'N/A';
        return `${programName} (${yearName}, ${semesterName})`;
      });
    } else if (series.programs && Array.isArray(series.programs)) {
      // Legacy structure: just programs with series-level year/semester
      const yearName = series.yearOfStudy?.name || 'N/A';
      const semesterName = series.semester?.name || 'N/A';
      programDetails = series.programs.map(prog => 
        `${prog.name} (${yearName}, ${semesterName})`
      );
    }
    
    const maxVisible = 2;
    const hasMany = programDetails.length > maxVisible;
    const visibleDetails = hasMany ? programDetails.slice(0, maxVisible).join(', ') : programDetails.join(', ');
    const moreCount = hasMany ? programDetails.length - maxVisible : 0;

    return (
      <>
        <td>{(pageIndex * pageSize) + idx + 1}</td>
        <td>{series.name}</td>
        <td>{formatDate(series.startDate)}</td>
        <td>
          {formatDate(series.endDate)}
          {series.closed ? (
            <div><span className="badge bg-secondary mt-1">Closed</span></div>
          ) : series.expired ? (
            <div>
              <span className="badge bg-warning text-dark mt-1">Expired</span>
              {completionSummaries[series.id]?.incompleteAssignments > 0 && (
                <span className="badge bg-danger mt-1 ms-1">
                  {completionSummaries[series.id].incompleteAssignments} incomplete
                </span>
              )}
            </div>
          ) : null}
        </td>
        <td
          className="programs-cell"
          style={{ position: 'relative', cursor: 'pointer' }}
          onClick={(e) => {
            e.stopPropagation();
            // Get the bounding rectangle of the clicked cell.
            const rect = e.currentTarget.getBoundingClientRect();
            // Toggle the popover: close if already open for this series.
            if (activePopoverData && activePopoverData.id === series.id) {
              setActivePopoverData(null);
            } else {
              setActivePopoverData({
                id: series.id,
                rect,
                programDetails,
              });
            }
          }}
        >
          <span>{visibleDetails}{hasMany && `, and ${moreCount} more`}</span>
        </td>
        <td>
          {!series.eimsFetchEnabled ? (
            <span className="text-muted">Disabled</span>
          ) : (
            <>
              <span className={`badge ${series.lastSyncStatus === 'SUCCESS' ? 'bg-success' : series.lastSyncStatus === 'PARTIAL' ? 'bg-warning text-dark' : series.lastSyncStatus === 'FAILED' ? 'bg-danger' : 'bg-secondary'}`}>
                {series.lastSyncStatus || 'Not synced yet'}
              </span>
              {series.lastSyncedAt && (
                <div className="small text-muted">{formatDate(series.lastSyncedAt)}</div>
              )}
            </>
          )}
        </td>
        <td>
          <button className="btn btn-sm btn-primary me-2" onClick={() => navigate(`/admin/exam-series/edit/${series.id}`)}>Edit</button>
          <button className="btn btn-sm btn-danger me-2" onClick={() => setDeleteId(series.id)}>Delete</button>
          {canCloseSeries && series.expired && !series.closed && (
            <button className="btn btn-sm btn-outline-secondary" onClick={() => handleClose(series)}>Close Series</button>
          )}
        </td>
      </>
    );
  };

  // Render the popover via a React portal.
  const popoverMaxWidth = Math.min(500, window.innerWidth - 16);
  const popover = activePopoverData && ReactDOM.createPortal(
    <div
      className="programs-popover"
      style={{
        position: 'fixed',
        left: Math.max(8, Math.min(activePopoverData.rect.left, window.innerWidth - popoverMaxWidth - 8)),
        top: activePopoverData.rect.bottom + 5,
        background: '#fff',
        border: '1px solid #ccc',
        padding: '5px',
        borderRadius: '5px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        zIndex: 1000,
        maxWidth: popoverMaxWidth,
        width: 'max-content',
        wordBreak: 'break-word',
      }}
    >
      <ul style={{ margin: 0, padding: 0, listStyleType: 'numbered' }}>
        {activePopoverData.programDetails.map((detail, index) => (
          <li key={index} style={{ padding: '2px 0' }}>{detail}</li>
        ))}
      </ul>
    </div>,
    document.body
  );

  return (
    <div className="container mt-2">
      <TitleWithBack title="Exam Series" />
      <div className="mb-3">
        <input
          type="text"
          className="form-control"
          placeholder="Search exam series..."
          value={keyword}
          onChange={(e) => { setKeyword(e.target.value); setPage(0); }}
        />
      </div>
      {loading ? (
        <p>Loading series...</p>
      ) : (
        <>
          {sortedSeries.length === 0 ? (
            <p>Nothing to show here</p>
          ) : (
            <PaginatedTable data={sortedSeries} columns={columns} renderRow={renderRow} rowsPerPage={50} />
          )}
          <div className="d-flex justify-content-between mt-3">
            <button className="btn btn-secondary" disabled={page === 0} onClick={() => setPage(page - 1)}>Prev</button>
            <span>Page {page + 1} of {totalPages}</span>
            <button className="btn btn-secondary" disabled={page + 1 >= totalPages} onClick={() => setPage(page + 1)}>Next</button>
          </div>
        </>
      )}
      {tooltip && (
        <div className="mt-3 alert alert-info">
          {tooltip}
        </div>
      )}
      {deleteId && (
        <ConfirmationModal
          title="Confirm Delete"
          message="Are you sure you want to delete this series?"
          onConfirm={() => handleDelete(deleteId)}
          onCancel={() => setDeleteId(null)}
        />
      )}
      {popover}
    </div>
  );
}

export default SeriesView;
